import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import XDate from 'xdate';
import { OPERATORS, TYPES } from 'consts/filters';
import { withStyleSheet } from 'hoc/styles';
import { DashFilterButton } from 'components/DashFilterButton';
import { Div } from 'components/Div';
import { Text } from 'components/Text';
import { Icon } from 'components/Icon';
import { Tooltip } from 'components/Tooltip';
import { DatePicker } from 'components/DatePicker';
import { get } from 'utils';
import { styleSheet } from './stylesheet';

class DashFilterButtons extends PureComponent {
    static propTypes = {
        canScrollLeft: PropTypes.bool.isRequired,
        canScrollRight: PropTypes.bool.isRequired,
        dashBookmarkTags: PropTypes.arrayOf(PropTypes.any),
        dashFilters: PropTypes.arrayOf(PropTypes.any),
        dateRange: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])),
        equityScope: PropTypes.arrayOf(PropTypes.any),
        handleButtonRefs: PropTypes.func.isRequired,
        handleScrollContainer: PropTypes.func.isRequired,
        onChange: PropTypes.func,
        onRemove: PropTypes.func,
        onScroll: PropTypes.func.isRequired,
        removeBookmarkTag: PropTypes.func.isRequired,
        removeDateRange: PropTypes.func.isRequired,
        removeEquityScope: PropTypes.func.isRequired,
        passedStyles: PropTypes.objectOf(PropTypes.any),
        scrollLeft: PropTypes.func.isRequired,
        scrollRight: PropTypes.func.isRequired,
        styles: PropTypes.objectOf(PropTypes.any).isRequired,
        theme: PropTypes.objectOf(PropTypes.any).isRequired
    };

    static defaultProps = {
        dashBookmarkTags: null,
        dashFilters: null,
        dateRange: null,
        equityScope: null,
        onChange: undefined,
        onRemove: null,
        passedStyles: {}
    };

    constructor(props) {
        super(props);

        this.renderFilterButton = this.renderFilterButton.bind(this);
    }

    renderBookmarkTags() {
        const { dashBookmarkTags, handleButtonRefs, removeBookmarkTag, styles, theme } = this.props;
        if (!get(dashBookmarkTags, 'length')) return null;
        return dashBookmarkTags.map((tag, idx) => (
            <Div
                key={tag}
                onClick={() => removeBookmarkTag(tag)}
                ref={node => handleButtonRefs(node, 'dashBookmarkTags', idx)}
                styles={styles.bookmarkTag}
            >
                <Text>{tag}</Text>
                <Div styles={styles.removeIcon} className="removeIcon">
                    <Icon type="circleX" color={theme.colors.white01} />
                </Div>
            </Div>
        ));
    }

    renderFilterButton(filter, idx) {
        const { onRemove, styles, handleButtonRefs } = this.props;
        if (filter.type === TYPES.eventGroup) {
            return (
                <DashFilterButton
                    ref={node => handleButtonRefs(node, 'filters', idx)}
                    styles={styles.eventGroup}
                    onRemove={() => onRemove(filter)}
                    eventGroupId={filter.value}
                />
            );
        }
        return null;
    }

    renderDateRange() {
        const { styles, dateRange, removeDateRange, theme, handleButtonRefs, onChange } = this.props;

        if (dateRange) {
            const start = new XDate(dateRange[0]);
            const end = new XDate(dateRange[1]);
            const endDate = end.toString('MMM d, yyyy');
            let startDate = start.toString('MMM d');

            if (start.toString('yyyy') !== end.toString('yyyy')) {
                startDate = start.toString('MMM d, yyyy');
            }

            const picker = ({ hideTooltip }) => (
                <DatePicker
                    name="dashDateRange"
                    range
                    onChange={e => {
                        onChange(e);
                        hideTooltip();
                    }}
                />
            );

            return (
                <Tooltip
                    isEnabled
                    useElementOffsetBottom
                    useElementOffsetLeft
                    xOffset={0}
                    yOffset={8}
                    persistOnMouseExit
                    blockBackground
                    useOutsideClickHandler
                    styles={styles.dateRangePicker}
                    content={picker}
                >
                    {({ showTooltip }) => (
                        <Div
                            styles={styles.dateRange}
                            onClick={showTooltip}
                            ref={node => handleButtonRefs(node, 'dateRange', 0)}
                            className="dashFilterButton"
                        >
                            <Div styles={styles.editIcon} className="editIcon">
                                <Icon type="pencil02" color="#efcac8" />
                            </Div>
                            <Text size={1}>
                                {startDate} — {endDate}
                            </Text>
                            <Div
                                styles={styles.removeIcon}
                                className="removeIcon"
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    removeDateRange(e);
                                }}
                            >
                                <Icon type="circleX" color={theme.colors.white01} />
                            </Div>
                        </Div>
                    )}
                </Tooltip>
            );
        }

        return null;
    }

    renderEquityScope() {
        const { styles, equityScope, removeEquityScope, theme, handleButtonRefs } = this.props;

        if (equityScope && equityScope.length > 0) {
            return equityScope.map(({ label, type, value, operator }, index) => {
                const key = get(value, 'gicsSubSectorId', get(value, 'gicsSectorId', value));
                const formattedLabel = label ? (operator === OPERATORS.isNot ? `NOT ${label}` : label) : null;
                let equityScopeStyle = styles.equity;
                if (type === 'sector') {
                    equityScopeStyle = styles.sector;
                }
                if (type === 'watchlist') {
                    equityScopeStyle = styles.watchlist;
                }
                if (type === 'index') {
                    equityScopeStyle = styles.index;
                }
                return (
                    <Div
                        key={`${type}-${key}`}
                        styles={equityScopeStyle}
                        onClick={event => removeEquityScope({ type, value, event })}
                        ref={node => handleButtonRefs(node, 'equityScope', index)}
                        className="dashFilterButton"
                    >
                        <Text>{formattedLabel || `Loading ${type}...`}</Text>
                        <Div styles={styles.removeIcon} className="removeIcon">
                            <Icon type="circleX" color={theme.colors.white01} />
                        </Div>
                    </Div>
                );
            });
        }

        return null;
    }

    render() {
        const {
            canScrollLeft,
            canScrollRight,
            dashFilters,
            handleScrollContainer,
            onScroll,
            passedStyles,
            scrollLeft,
            scrollRight,
            styles,
            theme
        } = this.props;
        const showArrows = canScrollRight || canScrollLeft;
        return (
            <Div styles={{ ...styles.container, ...passedStyles }} className="dashFilterButtons">
                {showArrows && (
                    <Div
                        styles={canScrollLeft ? styles.scrollArrowLeft : styles.scrollArrowLeftDisabled}
                        onClick={canScrollLeft ? scrollLeft : undefined}
                    >
                        <Icon type="chevron" color={canScrollLeft ? theme.colors.blue08 : theme.colors.gray08} />
                    </Div>
                )}
                <Div
                    styles={showArrows ? styles.scrollContainerWithArrows : styles.scrollContainer}
                    ref={handleScrollContainer}
                    onScroll={onScroll}
                >
                    {this.renderDateRange()}
                    {this.renderEquityScope()}
                    {(dashFilters || []).map(this.renderFilterButton)}
                    {this.renderBookmarkTags()}
                </Div>
                {showArrows && (
                    <Div
                        styles={canScrollRight ? styles.scrollArrowRight : styles.scrollArrowRightDisabled}
                        onClick={canScrollRight ? scrollRight : undefined}
                    >
                        <Icon type="chevron" color={canScrollRight ? theme.colors.blue08 : theme.colors.gray08} />
                    </Div>
                )}
            </Div>
        );
    }
}

export const DashFilterButtonsUI = compose(withStyleSheet(styleSheet))(DashFilterButtons);
